import { useTranslation } from "react-i18next";

import { Badge, Input, Switch } from "../../../components/ui";
import { CENTINELA_SCHEDULE_DAYS } from "../../../features/centinela/types";
import type {
  CentinelaSchedule,
  CentinelaScheduleDay,
} from "../../../features/centinela/types";
import type { Tone } from "../../../components/ui";

const DEFAULT_SCHEDULE: CentinelaSchedule = {
  activo: false,
  dias: [...CENTINELA_SCHEDULE_DAYS],
  desde: "00:00",
  hasta: "23:59",
};

const SHIFT_TONES: Record<string, Tone> = {
  en_turno: "success",
  fuera_de_turno: "warning",
  sin_horario: "neutral",
};

export const scheduleRangeReady = (schedule: CentinelaSchedule | null) =>
  !schedule?.activo || Boolean(schedule.desde && schedule.hasta);

export interface RecipientScheduleProps {
  schedule: CentinelaSchedule | null;
  onShift: boolean | null;
  shiftReason: string | null;
  disabled?: boolean;
  quietWindow?: { from: string; to: string } | null;
  onChange: (schedule: CentinelaSchedule) => void;
}

export default function RecipientSchedule({
  schedule,
  onShift,
  shiftReason,
  disabled = false,
  quietWindow = null,
  onChange,
}: RecipientScheduleProps) {
  const { t } = useTranslation("common");

  const current = schedule ?? DEFAULT_SCHEDULE;

  const patch = (changes: Partial<CentinelaSchedule>) =>
    onChange({ ...current, ...changes });

  const toggleDay = (day: CentinelaScheduleDay) => {
    const next = current.dias.includes(day)
      ? current.dias.filter((item) => item !== day)
      : [...current.dias, day];

    if (next.length === 0) return;

    patch({ dias: CENTINELA_SCHEDULE_DAYS.filter((item) => next.includes(item)) });
  };

  return (
    <div className="mt-3 space-y-2 rounded-lg border border-line-soft bg-app p-2.5 max-sm:space-y-3 max-sm:p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-[11.5px] font-medium text-content">
          <i className="ri-time-line text-[13px] text-content-secondary" />
          {t("monitoreoIa.notifications.scheduleLabel")}
        </span>

        <div className="flex items-center gap-2">
          {shiftReason ? (
            <Badge tone={SHIFT_TONES[shiftReason] ?? "neutral"} size="sm">
              {t(`monitoreoIa.notifications.shift.${shiftReason}`, {
                defaultValue: onShift
                  ? t("monitoreoIa.notifications.shift.en_turno")
                  : t("monitoreoIa.notifications.shift.fuera_de_turno"),
              })}
            </Badge>
          ) : null}

          <Switch
            size="sm"
            checked={current.activo}
            disabled={disabled}
            aria-label={t("monitoreoIa.notifications.scheduleLabel")}
            onChange={(checked) => patch({ activo: checked })}
          />
        </div>
      </div>

      {current.activo ? (
        <>
          <div className="flex flex-wrap gap-1 max-sm:gap-2">
            {CENTINELA_SCHEDULE_DAYS.map((day) => {
              const picked = current.dias.includes(day);

              return (
                <button
                  key={day}
                  type="button"
                  disabled={disabled}
                  onClick={() => toggleDay(day)}
                  aria-pressed={picked}
                  className={`h-6 w-6 rounded-md text-[10.5px] font-bold transition-colors max-sm:h-9 max-sm:w-9 max-sm:text-xs disabled:cursor-not-allowed disabled:opacity-60 ${
                    picked
                      ? "bg-primary-500 text-on-primary"
                      : "bg-subtle text-content-secondary hover:bg-line"
                  }`}
                >
                  {t(`monitoreoIa.notifications.days.${day}`, { defaultValue: day })}
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-2 gap-2 max-sm:gap-3">
            <Input
              size="sm"
              type="time"
              containerClassName="min-w-0"
              label={t("monitoreoIa.notifications.scheduleFrom")}
              value={current.desde}
              disabled={disabled}
              onChange={(event) => patch({ desde: event.target.value })}
            />
            <Input
              size="sm"
              type="time"
              containerClassName="min-w-0"
              label={t("monitoreoIa.notifications.scheduleTo")}
              value={current.hasta}
              disabled={disabled}
              onChange={(event) => patch({ hasta: event.target.value })}
            />
          </div>

          {scheduleRangeReady(current) ? null : (
            <p className="text-[10px] text-warning">
              {t("monitoreoIa.notifications.scheduleNeedsRange")}
            </p>
          )}

          <p className="text-[10px] leading-relaxed text-content-faint max-sm:text-xs">
            {t("monitoreoIa.notifications.scheduleHint")}
          </p>
        </>
      ) : (
        <p className="text-[10px] leading-relaxed text-content-faint max-sm:text-xs">
          {quietWindow
            ? t("monitoreoIa.notifications.scheduleQuietFallback", {
                from: quietWindow.from,
                to: quietWindow.to,
              })
            : t("monitoreoIa.notifications.scheduleAlwaysHint")}
        </p>
      )}
    </div>
  );
}
